import { EMAIL_MAX_LENGTH } from '@/lib/email-constants';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { createOptionalPhoneSchema, createRequiredPhoneSchema, normalizeOptionalPhone } from '@/lib/phone-validation';
import type { SCAPIShopperBasketsV2Schemas } from '@/types/scapi';

export const PRODUCT_GIFT_MESSAGE_MAX_LENGTH = 200;

export type ProductGiftDetails = {
    recipientName: string;
    recipientEmail: string;
    recipientPhone?: string;
    message?: string;
};

export interface ProductGiftSchemaOptions {
    requiresPhone: boolean;
    validateOptionalPhone?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createProductGiftSchema(t: TFunction<any, any>, options: ProductGiftSchemaOptions) {
    const phoneSchema = options.requiresPhone
        ? createRequiredPhoneSchema({
              required: t('product:gift.recipientPhoneRequired'),
              invalid: t('product:gift.recipientPhoneInvalid'),
          })
        : options.validateOptionalPhone
          ? createOptionalPhoneSchema({
                invalid: t('product:gift.recipientPhoneInvalid'),
            })
          : z.string().trim();

    return z.object({
        recipientName: z
            .string()
            .trim()
            .min(1, { message: t('product:gift.recipientNameRequired') }),
        recipientEmail: z
            .string()
            .trim()
            .min(1, { message: t('product:gift.recipientEmailRequired') })
            .max(EMAIL_MAX_LENGTH, t('product:gift.recipientEmailTooLong', { max: EMAIL_MAX_LENGTH }))
            .email({ message: t('product:gift.recipientEmailInvalid') }),
        recipientPhone: phoneSchema,
        message: z
            .string()
            .trim()
            .max(PRODUCT_GIFT_MESSAGE_MAX_LENGTH, {
                message: t('product:gift.messageMaxLength', {
                    max: PRODUCT_GIFT_MESSAGE_MAX_LENGTH,
                }),
            }),
    });
}

export type ProductGiftFormValues = z.infer<ReturnType<typeof createProductGiftSchema>>;

/**
 * Maps saved gift recipient details to SCAPI ProductLineItem custom attributes.
 */
export function mapProductGiftToLineItemAttributes(
    gift: ProductGiftDetails
): Partial<
    Pick<
        SCAPIShopperBasketsV2Schemas['ProductItem'],
        'c_isGift' | 'c_giftRecipientName' | 'c_giftRecipientEmail' | 'c_giftRecipientPhone' | 'c_giftMessage'
    >
> {
    const normalizedPhone = normalizeOptionalPhone(gift.recipientPhone);
    const trimmedMessage = gift.message?.trim();

    return {
        c_isGift: true,
        c_giftRecipientName: gift.recipientName.trim(),
        c_giftRecipientEmail: gift.recipientEmail.trim(),
        ...(normalizedPhone ? { c_giftRecipientPhone: normalizedPhone } : {}),
        ...(trimmedMessage ? { c_giftMessage: trimmedMessage } : {}),
    };
}
